"use client";

import { Link } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { downloadReport } from "@/lib/report-download";
import { Button, buttonVariants } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import { ArrowLeft } from "lucide-react";

export default function StockReportPage() {
  const [productId, setProductId] = useState("");

  const { data: val } = useQuery({
    queryKey: ["reports", "stock-valuation"],
    queryFn: async () => {
      const { data: d } = await api.get<{
        rows: Record<string, unknown>[];
        summary: { total_cost_value: number; total_selling_value: number; potential_profit: number };
      }>("/api/reports/stock-valuation");
      return d;
    },
  });

  const { data: exp } = useQuery({
    queryKey: ["reports", "expiry"],
    queryFn: async () => {
      const { data: d } = await api.get<{ rows: Record<string, unknown>[] }>("/api/reports/expiry");
      return d.rows;
    },
  });

  const pid = Number(productId);
  const { data: mov } = useQuery({
    queryKey: ["stock", "movements", pid],
    enabled: pid > 0,
    queryFn: async () => {
      const { data: d } = await api.get<{ movements: Record<string, unknown>[] }>(
        `/api/stock/movements/${pid}`,
      );
      return d.movements;
    },
  });

  return (
    <div className="space-y-6 p-6">
      <Link
        to="/reports"
        className={cn(buttonVariants({ variant: "ghost", size: "sm" }), "gap-2 inline-flex")}
      >
        <ArrowLeft className="h-4 w-4" />
        Reports
      </Link>
      <h1 className="text-2xl font-semibold tracking-tight">Stock valuation & expiry</h1>
      <div className="flex gap-2">
        <Button type="button" variant="outline" onClick={() => void downloadReport("/api/reports/stock-valuation/export", {}, "stock-valuation.xlsx")}>
          Export Excel
        </Button>
      </div>

      {val?.summary && (
        <div className="flex flex-wrap gap-4 text-sm">
          <span>Cost value ₹{val.summary.total_cost_value.toFixed(2)}</span>
          <span>Selling value ₹{val.summary.total_selling_value.toFixed(2)}</span>
          <span className="font-medium">Potential ₹{val.summary.potential_profit.toFixed(2)}</span>
        </div>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Valuation (qty &gt; 0)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto max-h-[280px] overflow-y-auto rounded-md border">
          <table className="w-full text-xs">
            <thead className="bg-muted/50 sticky top-0">
              <tr>
                <th className="p-2 text-left">Product</th>
                <th className="p-2 text-right">Qty</th>
                <th className="p-2 text-right">Cost val</th>
                <th className="p-2 text-right">Sell val</th>
              </tr>
            </thead>
            <tbody>
              {(val?.rows ?? []).map((r) => (
                <tr key={String(r.id)} className="border-t">
                  <td className="p-2">{String(r.name)}</td>
                  <td className="p-2 text-right tabular-nums">{Number(r.stock_qty).toFixed(3)}</td>
                  <td className="p-2 text-right tabular-nums">{Number(r.cost_value).toFixed(2)}</td>
                  <td className="p-2 text-right tabular-nums">{Number(r.selling_value).toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Expiry (next 30 days)</CardTitle>
        </CardHeader>
        <CardContent className="overflow-x-auto max-h-[220px] overflow-y-auto rounded-md border text-xs">
          <table className="w-full">
            <thead className="bg-muted/50">
              <tr>
                <th className="p-2 text-left">Product</th>
                <th className="p-2 text-left">Expiry</th>
                <th className="p-2 text-left">GRN</th>
              </tr>
            </thead>
            <tbody>
              {(exp ?? []).map((r) => (
                <tr key={String(r.id)} className="border-t">
                  <td className="p-2">{String(r.product_name)}</td>
                  <td className="p-2">{String(r.expiry_date)}</td>
                  <td className="p-2">{String(r.grn_no)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Movement history</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="grid gap-1.5 max-w-xs">
            <Label className="text-xs">Product ID</Label>
            <Input value={productId} onChange={(e) => setProductId(e.target.value)} placeholder="e.g. 12" />
          </div>
          <div className="max-h-[240px] overflow-y-auto rounded-md border text-xs">
            <table className="w-full">
              <thead className="bg-muted/50 sticky top-0">
                <tr>
                  <th className="p-2 text-left">When</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-right">Δ qty</th>
                </tr>
              </thead>
              <tbody>
                {(mov ?? []).map((m) => (
                  <tr key={String(m.id)} className="border-t">
                    <td className="p-2 whitespace-nowrap">{String(m.created_at)}</td>
                    <td className="p-2">{String(m.movement_type)}</td>
                    <td className="p-2 text-right tabular-nums">{String(m.qty_change)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {pid <= 0 && <p className="p-3 text-muted-foreground">Enter product id to load movements.</p>}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
